import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

// High-Fidelity Asset Pipeline (Production Layout Standard)
import botal_1_1ltr from "../media/botal_1_1ltr.jpeg";
import botal_2_1ltr from "../media/botal_2_1ltr.jpeg";
import botal_3_1ltr from "../media/botal_3_1ltr.jpeg";

import botal_1_2ltr from "../media/botal_1_2ltr.jpeg";
import botal_2_2ltr from "../media/botal_2_2ltr.jpeg";
import botal_3_2ltr from "../media/botal_3_2ltr.jpeg";

import botal_1_500ml from "../media/botal_1_500ml.jpeg";
import botal_2_500ml from "../media/botal_2_500ml.jpeg";
import botal_3_500ml from "../media/botal_3_500ml.jpeg";

import botal_1_250ml from "../media/botal_1_250ml.jpeg";
import botal_2_250ml from "../media/botal_2_250ml.jpeg";
import botal_3_250ml from "../media/botal_3_250ml.jpeg";

const Product = () => {
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [filterSize, setFilterSize] = useState("ALL");
  const [bulkMode, setBulkMode] = useState(false);

  // Strategic Product Core Matrix - Director level market classification
  const products = [
    {
      id: "prod_water_250ml",
      name: "Corporate Micro 250ml Asset",
      tagline: "Highest Market Velocity - Corporate Choice",
      description:
        "Ultra-portable organic micro-capsule engineered for scale supply vectors.",
      fullDescription:
        "Our 250ml product segment currently commands peak industry demand across corporate summits, luxury logistics, and massive public catering networks. Fits seamlessly into large-scale rapid delivery systems while generating zero packaging weight overhead.",
      priceB2C: "₹6",
      priceB2B: "₹4.20 per unit (Min. 500 Units)",
      priceNumber: 6,
      size: "250ml",
      isHighDemand: true, // Strategic high light flag
      image: [botal_1_250ml, botal_2_250ml, botal_3_250ml],
      metrics: "98% Client Retention Metric",
    },
    {
      id: "prod_water_500ml",
      name: "Standard Hydration 500ml Package",
      tagline: "Daily Active Moving Consumable",
      description:
        "Optimized daily use packaging built for field operations and mobility.",
      fullDescription:
        "The absolute benchmark for individual distribution pipelines. Perfect volume balance optimized for fitness infrastructures, standard hospitality clusters, and immediate workspace consumption lines.",
      priceB2C: "₹10",
      priceB2B: "₹7.50 per unit (Min. 200 Units)",
      priceNumber: 10,
      size: "500ml",
      isHighDemand: false,
      image: [botal_1_500ml, botal_2_500ml, botal_3_500ml],
      metrics: "Standard Fleet Deployment Asset",
    },
    {
      id: "prod_water_1ltr",
      name: "Enterprise 1L Core Container",
      tagline: "High-Fidelity Mineral Balance",
      description:
        "Signature 1-Liter resource vessel for complete operational cycles.",
      fullDescription:
        "Engineered for full-day sustainability goals. Multi-filtered mineral distribution lines make this the default infrastructure requirement for core industrial workspace environments and medical facilities.",
      priceB2C: "₹20",
      priceB2B: "₹14.80 per unit (Min. 100 Units)",
      priceNumber: 20,
      size: "1L",
      isHighDemand: false,
      image: [botal_1_1ltr, botal_2_1ltr, botal_3_1ltr],
      metrics: "Certified Eco-Safe Standard",
    },
    {
      id: "prod_water_2ltr",
      name: "High-Capacity 2L Bulk Vessel",
      tagline: "Heavy Resource Sustainability Unit",
      description:
        "Max volume fluid storage for emergency reserve & family modules.",
      fullDescription:
        "High-capacity heavy layout packaging intended to anchor remote disaster relief operations, community camp clusters, and high-intensity field requirements with long stability life parameters.",
      priceB2C: "₹30",
      priceB2B: "₹23.00 per unit (Min. 50 Units)",
      priceNumber: 30,
      size: "2L",
      isHighDemand: false,
      image: [botal_1_2ltr, botal_2_2ltr, botal_3_2ltr],
      metrics: "Disaster Deployment Approved",
    },
  ];

  // Dynamic Image Rotation Sequence inside Active Modal Context
  useEffect(() => {
    let automaticTimer;
    if (modalOpen && selectedProduct) {
      automaticTimer = setInterval(() => {
        setCurrentImageIndex(
          (prev) => (prev + 1) % selectedProduct.image.length
        );
      }, 3000);
    } else {
      setCurrentImageIndex(0);
    }
    return () => clearInterval(automaticTimer);
  }, [modalOpen, selectedProduct]);

  // Deep Semantic SEO / GEO / AEO Structured Graph Markup
  const corporateInventorySchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "NGO Waters",
    url: "https://ngowaters.org",
    logo: "https://ngowaters.org/media/logo.png",
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Eco Hydration Resource Infrastructure Solutions",
      itemListElement: products.map((item) => ({
        "@type": "Offer",
        itemOffered: {
          "@type": "Product",
          name: item.name,
          sku: item.id,
          description: item.description,
          image: item.image[0],
          offers: {
            "@type": "AggregateOffer",
            priceCurrency: "INR",
            lowPrice: "4.20",
            highPrice: "30.00",
            offerCount: "2",
          },
        },
      })),
    },
  };

  // Fluid UI Array Filtration Logic
  const filteredInventory =
    filterSize === "ALL"
      ? products
      : products.filter((p) => p.size === filterSize);

  return (
    <section
      className="position-relative py-5 overflow-hidden min-vh-100 d-flex align-items-center"
      style={{
        background:
          "linear-gradient(135deg, var(--brand-pure-white) 0%, var(--brand-sky-blue) 100%)",
      }}
    >
      {/* Structural Microdata Architecture for Edge Web Crawlers and LLM Parsers */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(corporateInventorySchema),
        }}
      />

      {/* Corporate Accent Glass Blur Overlays */}
      <div
        className="position-absolute rounded-circle opacity-25"
        style={{
          width: "600px",
          height: "600px",
          top: "-15%",
          right: "-10%",
          background: "var(--brand-mineral-aqua)",
          filter: "blur(150px)",
          pointerEvents: "none",
        }}
      />
      <div
        className="position-absolute rounded-circle opacity-25"
        style={{
          width: "400px",
          height: "400px",
          bottom: "-10%",
          left: "-10%",
          background: "var(--brand-copper)",
          filter: "blur(120px)",
          pointerEvents: "none",
        }}
      />

      <div className="container position-relative z-1 py-5">
        {/* Upper Brand Control Core Block */}
        <div className="row justify-content-center text-center mb-5">
          <div className="col-12 col-lg-9">
            <span
              className="text-uppercase fw-bold px-3 py-1 rounded-pill small d-inline-block mb-3"
              style={{
                backgroundColor: "var(--brand-deep-blue)",
                color: "var(--brand-pure-white)",
                fontSize: "0.75rem",
                letterSpacing: "1.5px",
              }}
            >
              Enterprise Supply Chain Model (2026 Fleet)
            </span>
            <h1
              className="display-4 fw-bold mb-3"
              style={{ color: "var(--brand-deep-blue)" }}
            >
              Sustainable Micro-Hydration Infrastructure
            </h1>
            <p
              className="text-charcoal mx-auto opacity-85 lead fs-6 mb-4"
              style={{ maxWidth: "700px" }}
            >
              Direct procurement access for state-level NGOs, corporate
              workspace blocks, and high-velocity consumer sectors optimized for
              clean resource management.
            </p>

            {/* Strategic Controller Toggles - Dynamic Conversion Optimization */}
            <div className="d-flex flex-wrap justify-content-center align-items-center gap-3 mt-4">
              {/* Segmentation Pipeline Filter */}
              <div
                className="bg-white p-1 rounded-pill shadow-sm d-flex border"
                style={{ borderColor: "rgba(0,0,0,0.05)" }}
              >
                {["ALL", "250ml", "500ml", "1L", "2L"].map((size) => (
                  <button
                    key={size}
                    className={`btn btn-sm rounded-pill px-3 py-1.5 fw-semibold border-0 ${
                      filterSize === size ? "active" : ""
                    }`}
                    style={{
                      backgroundColor:
                        filterSize === size
                          ? "var(--brand-deep-blue)"
                          : "transparent",
                      color:
                        filterSize === size
                          ? "var(--brand-pure-white)"
                          : "var(--brand-charcoal)",
                      fontSize: "0.8rem",
                    }}
                    onClick={() => setFilterSize(size)}
                  >
                    {size}
                  </button>
                ))}
              </div>

              {/* B2B Pricing Paradigm Toggle */}
              <button
                className="btn rounded-pill px-4 py-2 fw-bold text-uppercase tracking-wider transition-all"
                style={{
                  backgroundColor: bulkMode
                    ? "var(--brand-copper)"
                    : "var(--brand-pure-white)",
                  color: bulkMode
                    ? "var(--brand-pure-white)"
                    : "var(--brand-deep-blue)",
                  border: "1.5px solid var(--brand-copper)",
                  fontSize: "0.78rem",
                  boxShadow: "0 4px 12px rgba(166, 123, 91, 0.15)",
                }}
                onClick={() => setBulkMode(!bulkMode)}
              >
                <i
                  className={`bi ${
                    bulkMode ? "bi-building-fill-check" : "bi-building"
                  } me-2`}
                ></i>
                {bulkMode
                  ? "Enterprise Logistics Pricing ON"
                  : "View Enterprise Bulk Pricing"}
              </button>
            </div>
          </div>
        </div>

        {/* Multi-Row Stagger Distribution Grid */}
        <motion.div className="row g-4 justify-content-center" layout>
          <AnimatePresence mode="popLayout">
            {filteredInventory.map((product) => (
              <motion.div
                className="col-12 col-md-6 col-lg-3 d-flex"
                key={product.id}
                layout
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ type: "spring", stiffness: 100, damping: 18 }}
              >
                <article
                  className="card w-100 border-0 rounded-4 shadow-sm overflow-hidden d-flex flex-column justify-content-between position-relative backdrop-blur"
                  style={{
                    background: product.isHighDemand
                      ? "rgba(255, 255, 255, 0.85)"
                      : "rgba(255, 255, 255, 0.55)",
                    border: product.isHighDemand
                      ? "2px solid var(--brand-copper)"
                      : "1px solid rgba(255, 255, 255, 0.4)",
                    boxShadow: product.isHighDemand
                      ? "0 20px 45px rgba(166, 123, 91, 0.2)"
                      : "0 10px 30px rgba(0, 0, 0, 0.05)",
                  }}
                >
                  {/* Strategic Micro-Badging Indicators */}
                  <div
                    className="position-absolute top-0 end-0 z-1 px-3 py-1.5 fw-bold rounded-start-4 text-white"
                    style={{
                      background: product.isHighDemand
                        ? "var(--brand-copper)"
                        : "var(--brand-deep-blue)",
                      fontSize: "0.85rem",
                    }}
                  >
                    {bulkMode
                      ? "B2B Mode"
                      : product.isHighDemand
                      ? "🔥 High Velocity"
                      : product.size}
                  </div>

                  <div>
                    {/* Visual Asset Section Container */}
                    <div
                      className="position-relative overflow-hidden"
                      style={{
                        height: "230px",
                        background: "rgba(255,255,255,0.3)",
                      }}
                    >
                      <img
                        src={product.image[0]}
                        className="w-100 h-100"
                        alt={product.name}
                        loading="lazy"
                        style={{
                          objectFit: "cover",
                          transition: "var(--slomo-transition)",
                        }}
                      />
                    </div>

                    {/* Metadata Content Engine */}
                    <div className="p-4">
                      <div className="d-flex align-items-center mb-1">
                        <small
                          className="fw-bold tracking-wide text-uppercase text-muted"
                          style={{
                            fontSize: "0.68rem",
                            color: "var(--brand-copper) !important",
                          }}
                        >
                          {product.tagline}
                        </small>
                      </div>
                      <h5
                        className="h6 fw-bold mb-2 text-dark"
                        style={{ letterSpacing: "-0.3px" }}
                      >
                        {product.name}
                      </h5>
                      <p
                        className="small mb-0 text-charcoal opacity-75"
                        style={{ minHeight: "36px", lineHeight: "1.5" }}
                      >
                        {product.description}
                      </p>
                    </div>
                  </div>

                  {/* Pricing Matrix Block & Action Controls */}
                  <div className="px-4 pb-4 pt-0">
                    <div
                      className="bg-white p-3 rounded-3 mb-3 text-center border"
                      style={{ borderColor: "rgba(0,0,0,0.03)" }}
                    >
                      <span
                        className="small text-muted d-block mb-1"
                        style={{ fontSize: "0.72rem" }}
                      >
                        Target Allocation Cost
                      </span>
                      <strong
                        className="h5 fw-bold m-0 d-block"
                        style={{ color: "var(--brand-deep-blue)" }}
                      >
                        {bulkMode ? product.priceB2B : product.priceB2C}
                      </strong>
                      <span
                        className="badge mt-2 text-wrap opacity-70"
                        style={{
                          backgroundColor: "var(--brand-sky-blue)",
                          color: "var(--brand-deep-blue)",
                          fontSize: "0.68rem",
                        }}
                      >
                        {product.metrics}
                      </span>
                    </div>

                    <button
                      className="btn w-100 fw-bold rounded-pill py-2.5 text-uppercase tracking-wider btn-sm transition-all"
                      style={{
                        border: "1.5px solid var(--brand-deep-blue)",
                        backgroundColor: product.isHighDemand
                          ? "var(--brand-deep-blue)"
                          : "transparent",
                        color: product.isHighDemand
                          ? "var(--brand-pure-white)"
                          : "var(--brand-deep-blue)",
                        fontSize: "0.75rem",
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor =
                          "var(--brand-deep-blue)";
                        e.target.style.color = "var(--brand-pure-white)";
                      }}
                      onMouseLeave={(e) => {
                        if (!product.isHighDemand) {
                          e.target.style.backgroundColor = "transparent";
                          e.target.style.color = "var(--brand-deep-blue)";
                        }
                      }}
                      onClick={() => {
                        setSelectedProduct(product);
                        setModalOpen(true);
                      }}
                    >
                      Audit Analytics
                    </button>
                  </div>
                </article>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      {/* Framer Motion Immersive Data Center Modal View */}
      <AnimatePresence>
        {modalOpen && selectedProduct && (
          <motion.div
            className="modal d-block d-flex align-items-center justify-content-center p-3 position-fixed top-0 start-0 w-100 h-100 z-3"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              background: "rgba(26, 60, 107, 0.45)",
              backdropFilter: "blur(12px)",
              WebkitBackdropFilter: "blur(12px)",
            }}
            onClick={() => setModalOpen(false)}
          >
            <motion.div
              className="modal-dialog modal-dialog-centered w-100"
              style={{ maxWidth: "550px" }}
              initial={{ scale: 0.94, y: 20, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.94, y: 20, opacity: 0 }}
              transition={{ type: "spring", stiffness: 260, damping: 25 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                className="modal-content border-0 rounded-4 overflow-hidden position-relative"
                style={{
                  background: "rgba(255, 255, 255, 0.88)",
                  border: "1.5px solid var(--brand-sky-blue) !important",
                  boxShadow: "0 40px 90px rgba(0,0,0,0.35)",
                }}
              >
                {/* Modal Premium Header Banner */}
                <div
                  className="p-4 d-flex justify-content-between align-items-center border-bottom bg-white"
                  style={{ borderColor: "rgba(0,0,0,0.06)" }}
                >
                  <div>
                    <h5
                      className="h6 modal-title fw-bold m-0"
                      style={{ color: "var(--brand-deep-blue)" }}
                    >
                      {selectedProduct.name}
                    </h5>
                    <small
                      className="text-muted text-uppercase fw-semibold"
                      style={{
                        fontSize: "0.65rem",
                        color: "var(--brand-copper) !important",
                      }}
                    >
                      {selectedProduct.tagline}
                    </small>
                  </div>
                  <button
                    type="button"
                    className="btn-close shadow-none"
                    onClick={() => setModalOpen(false)}
                    aria-label="Terminate UI overlay logs"
                  ></button>
                </div>

                {/* Main Media Center Slider Body */}
                <div className="p-4">
                  <div
                    className="position-relative overflow-hidden rounded-3 mb-4 bg-white shadow-inner border"
                    style={{ height: "270px" }}
                  >
                    <AnimatePresence mode="wait">
                      <motion.img
                        key={currentImageIndex}
                        src={selectedProduct.image[currentImageIndex]}
                        alt={`${selectedProduct.name} dynamic analysis frame`}
                        className="w-100 h-100"
                        initial={{ opacity: 0, scale: 1.05 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.96 }}
                        transition={{ duration: 0.3 }}
                        style={{ objectFit: "contain" }}
                      />
                    </AnimatePresence>

                    {/* Image Pips Slide Tracker Navigation */}
                    <div className="position-absolute bottom-0 start-50 translate-middle-x d-flex gap-2 pb-3">
                      {selectedProduct.image.map((_, index) => (
                        <span
                          key={`indicator_pip_${index}`}
                          className="rounded-pill d-block"
                          style={{
                            width: index === currentImageIndex ? "16px" : "6px",
                            height: "6px",
                            background:
                              index === currentImageIndex
                                ? "var(--brand-mineral-aqua)"
                                : "rgba(0,0,0,0.15)",
                            transition: "all 0.3s ease",
                          }}
                        />
                      ))}
                    </div>
                  </div>

                  {/* Strategic Corporate Metrics Matrix */}
                  <div className="text-start">
                    <h6 className="small fw-bold text-uppercase tracking-wider text-dark mb-2">
                      Director's Operational Statement
                    </h6>
                    <p
                      className="small text-charcoal opacity-85 mb-4"
                      style={{ lineHeight: "1.65" }}
                    >
                      {selectedProduct.fullDescription}
                    </p>

                    <div
                      className="row g-2 border-top pt-3"
                      style={{ borderColor: "rgba(0,0,0,0.06)" }}
                    >
                      <div className="col-6">
                        <div className="bg-white p-2.5 rounded-3 border text-center">
                          <span
                            className="small text-muted d-block mb-0.5"
                            style={{ fontSize: "0.68rem" }}
                          >
                            Retail (B2C)
                          </span>
                          <strong style={{ color: "var(--brand-deep-blue)" }}>
                            {selectedProduct.priceB2C}
                          </strong>
                        </div>
                      </div>
                      <div className="col-6">
                        <div
                          className="p-2.5 rounded-3 border text-center"
                          style={{ backgroundColor: "var(--brand-sky-blue)" }}
                        >
                          <span
                            className="small text-dark opacity-70 d-block mb-0.5"
                            style={{ fontSize: "0.68rem" }}
                          >
                            Volume Unit Metrics
                          </span>
                          <strong style={{ color: "var(--brand-deep-blue)" }}>
                            {selectedProduct.size} Scale
                          </strong>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Immersive Allocation Footer Controls */}
                <div
                  className="p-4 bg-white d-flex gap-3 border-top"
                  style={{ borderColor: "rgba(0,0,0,0.05)" }}
                >
                  <button
                    type="button"
                    className="btn w-40 fw-semibold rounded-pill py-2.5 btn-sm border-0 text-muted"
                    onClick={() => setModalOpen(false)}
                  >
                    Close Pipeline
                  </button>
                  <motion.button
                    whileHover={{
                      scale: 1.02,
                      backgroundColor: "var(--brand-copper)",
                    }}
                    whileTap={{ scale: 0.98 }}
                    className="btn w-60 fw-bold rounded-pill py-2.5 btn-sm text-white text-uppercase tracking-wider border-0"
                    style={{
                      background: "var(--brand-deep-blue)",
                      fontSize: "0.75rem",
                      boxShadow: "0 10px 25px rgba(26, 60, 107, 0.25)",
                    }}
                  >
                    Lock Supply Contract (
                    {bulkMode ? "B2B Pricing" : selectedProduct.priceB2C})
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Product;
